import { useStrapiQuery } from 'hooks/useStrapiQuery';
import type { Pagination } from 'hooks/useTablePagination';
import { useMeters } from 'api/hooks';
import type { PopulatedOneMeter } from 'lib/services';
import { useMemo } from 'react';
import {
  GET_INVENTORY_METERS_LIST_DATA,
  type InventoryMeterData,
  type InventoryMeterQueryData,
  type InventoryMetersCardVariables,
} from '../queries/getInventoryMeterListData';
import { MetersCardTab } from '../enums/MeterCardTab';

function getQueryVariablesForTab(activeTab: MetersCardTab) {
  switch (activeTab) {
    case MetersCardTab.Pasport:
      return {
        sort: ['submitted_at:desc'],
        filter: { submitted_at: { notNull: true } },
      };
    case MetersCardTab.Install:
      return {
        sort: ['installed_at:desc'],
        filter: { installed_at: { notNull: true } },
      };
    case MetersCardTab.Service:
      return {
        sort: ['serviced_at:desc'],
        filter: { serviced_at: { notNull: true } },
      };
    case MetersCardTab.Replace:
      return {
        sort: ['replaced_at:desc'],
        filter: { replaced_at: { notNull: true } },
      };
    case MetersCardTab.Update:
      return {
        sort: ['updatedAt:desc'],
        filter: { updatedAt: { notNull: true } },
      };
    default:
      return {};
  }
}

export interface InventoryMeterListData {
  data: (InventoryMeterData & PopulatedOneMeter)[];
  total: number;
  loading: boolean;
}

export const useCardInventoryMeters = (
  activeMetersTab: MetersCardTab,
  paginationOptions: Pagination,
): InventoryMeterListData => {
  const variables: InventoryMetersCardVariables = {
    limit: paginationOptions.pageSize,
    start: (paginationOptions.current - 1) * paginationOptions.pageSize,
    ...getQueryVariablesForTab(activeMetersTab),
  };

  const { data: invMeterData, loading } = useStrapiQuery<InventoryMeterQueryData, InventoryMetersCardVariables>(
    GET_INVENTORY_METERS_LIST_DATA,
    {
      variables,
    },
  );

  const toolsIds = useMemo(
    () => invMeterData?.inventoryMeters.data.map(invMeter => invMeter.id) || [],
    [invMeterData?.inventoryMeters.data],
  );

  const { data: enmonMetersData, isFetching } = useMeters(
    {
      filter: {
        toolsIds,
      },
    },
    {
      enabled: !!invMeterData,
    },
  );

  const data = useMemo(
    () =>
      invMeterData?.inventoryMeters.data.map(invMeter => ({
        ...invMeter,
        ...enmonMetersData?.result.find(enmonMeter => enmonMeter.toolsId === invMeter.id.toString()),
      })),
    [enmonMetersData?.result, invMeterData?.inventoryMeters.data],
  );

  const total = invMeterData?.inventoryMeters.meta.pagination.total || 0;
  return { data, total, loading: loading || isFetching };
};
